import { Css, Icon, IconKey, Margin, Only, Palette, Properties, useTestIds, Xss } from "@homebound/beam";
import { ReactNode } from "react";

type InfoBannerVariant = "info" | "warning";

type InfoBannerProps<X> = { variant: InfoBannerVariant; title: string; message: ReactNode; xss?: X };

/** A boxed notice with a leading icon, a bold title and a line of copy, e.g. "Commitments are not updated automatically". */
export function InfoBanner<X extends Only<Xss<Margin>, X>>(props: InfoBannerProps<X>) {
  const { variant, title, message, xss } = props;
  const tid = useTestIds(props, "banner");
  const { icon, color, css } = variants[variant];
  return (
    <div css={{ ...Css.df.gap1.p2.br12.bw2.bss.$, ...css, ...xss }} {...tid}>
      <span css={Css.fs0.df.$}>
        <Icon icon={icon} inc={3} color={color} />
      </span>
      <div css={Css.df.fdc.gap1.$}>
        <span css={Css.mdSb.gray900.$} {...tid.title}>
          {title}
        </span>
        <span css={Css.sm.gray900.$} {...tid.message}>
          {message}
        </span>
      </div>
    </div>
  );
}

const variants: Record<InfoBannerVariant, { icon: IconKey; color: Palette; css: Properties }> = {
  info: { icon: "errorCircle", color: Palette.Blue600, css: Css.bgBlue50.bcBlue600.$ },
  // Beam has no `warning` icon; `error` is the triangle the app uses for orange/yellow warnings
  warning: { icon: "error", color: Palette.Orange700, css: Css.bgOrange50.bcOrange700.$ },
};
